/**
 * config/defaults.ts — Constrained 3D configurator default constants.
 *
 * Boundary rule (SPEC §6, PLAN §1.2): `config/` contains EVERY constant —
 * colors, heights, offsets, mappings — never hardcoded elsewhere. The configurator
 * is constrained: the values are centralized calibration points (PLAN §2.6).
 *
 * Initial values to visually calibrate (AC-1 test): `CONNECTOR_PLANE_HEIGHT_MM`,
 * `OFFSET_FROM_TOP`, `DEFAULT_BEAM_LENGTH_MM`. Don't change here without checking the scene.*/

/**
 * Height `h` of the connector plane (root of the world) from the X-Z plane (grid
 * y=0), in mm. The world settles around this plan (SPEC G1).
 * Initial value of the plan; to be calibrated visually.*/
export const CONNECTOR_PLANE_HEIGHT_MM = 300;

/**
 * Opacita' di default del materiale delle travi (0..1). Le travi sono il
 * riferimento, il connettore e' il soggetto: semitrasparenti lo lasciano
 * leggere anche quando e' incassato. `componentsState.opacity` la sovrascrive.*/
export const BEAM_OPACITY = 0.35;

/**
 * Contorno tecnico dei lock caricati da GLB. `thresholdDeg` e' l'angolo fra
 * due facce sotto il quale lo spigolo NON viene disegnato: alzarlo lascia solo
 * i profili netti, abbassarlo fa emergere anche la tassellazione delle
 * superfici curve (i bordi dei fori stanno molto sopra i 30 gradi).*/
export const CONNECTOR_EDGES = {
  thresholdDeg: 30,
  opacity: 0.9,
};

/**
 * Inquadratura sui CONNETTORI, non sull'intero assieme: le travi restano in
 * scena come contesto ma non decidono lo zoom. `marginFactor` e' l'aria attorno
 * alla sfera che contiene i connettori, `minRadiusMm` evita di avvicinarsi
 * troppo quando il connettore e' uno solo e piccolo. Due tarature perche' il
 * viewer interattivo vuole piu' respiro del PDF, dove l'immagine e' piccola.*/
export const FOCUS_FRAMING = {
  viewer: { marginFactor: 0.7, minRadiusMm: 140 },
  capture: { marginFactor: 0.7, minRadiusMm: 140 },
  /**
   * Margine (mm) oltre raggio-connettori + lunghezza-trave per la distanza
   * della camera ORTOGRAFICA (bug corretto 24-09-2026: le quote/travi in
   * fondo alla joist sparivano in vista "side"). In ortografica la distanza
   * NON cambia lo zoom (che dipende solo dal raggio, vedi `camera.zoom` in
   * `LockScene.tsx`) — serve solo a tenere la geometria DAVANTI alla camera:
   * con `distance = radius*4` (~560mm coi default) e una joist lunga 700mm,
   * l'estremita' lontana finiva oltre la posizione della camera (dietro,
   * clippata dal near-plane) proprio nella vista che guarda lungo la fibra
   * della trave. Aumentare la distanza e' quindi senza costo visivo.*/
  orthoClearanceMarginMm: 200,
} as const;

//─── Chain Calibration Parameters (SSOT) ───────────────
// I 3 TODO storici (R1 segno Ry, R7 ordine composizione, R2 segno flush)
//they are centralized here as documented CONSTANTS: they are calibrated ONCE
//in the AC-1 visual test by changing ONLY ONE point (SPEC G2/G5, PLAN §7).

/**
 * Authoring→viewer conversion rotation (euler degrees) per il lato JOIST,
 * Ry(-90°). Mappatura verificata con calcolo reale (three.js, non a mano —
 * vedi nota sotto): auth x → viewer +z, auth y → viewer y, auth z → viewer -x.
 *
 * IMPORTANTE (indagine incastro 25-09-2026 — questo segno vale SOLO per il
 * joist): il lato HEADER non riusa lo stesso valore. Essendo posizionato
 * specularmente rispetto al piano connettore condiviso, la sua conversione
 * naturale ha segno OPPOSTO, Ry(+90°) — vedi `transforms/connectorPlacement.ts`
 * (SSOT del posizionamento), che usa questa costante per il joist e la
 * versione negata per l'header.
 *
 * CORREZIONE 25-09-2026 (verificata con calcolo reale, non a mano — un
 * commento precedente affermava l'opposto senza controllo, mai riscontrato
 * nel valore effettivo della costante): col JOIST completo (questa
 * conversione + il flip mondiale Rx180 in `connectorPlacement.ts`), l'asse Z
 * locale finisce lungo il mondo **-X**; e' l'HEADER (`authoringToViewerQuatForHeader()`
 * da sola, nessun Rx180) ad avere l'asse Z lungo **+X**, cioe' allineato al
 * verso di `plane.normal` (SPEC G1) — non il joist, come un vecchio commento
 * (basato solo sui tripodi di debug, prima dell'aggiunta del Rx180)
 * affermava erroneamente. Questo NON e' un bug: l'unico criterio che conta
 * davvero e' il risultato VISIVO finale confermato dall'utente (viti nei
 * fori, connettori flush — vedi `model/connectorGoodConfig.test.ts`, che
 * verifica solo l'invariante RELATIVO — joist e header su versi opposti —
 * non una direzione assoluta arbitraria).
 *
 * Sole source: used by `transforms/authoringToViewer.ts` e
 * `transforms/connectorPlacement.ts`.*/
export const AUTHORING_TO_VIEWER_ROTATION_DEG = [0, -90, 0] as const;

/**
 * Flip mondiale (gradi, attorno a X) applicato SOLO al JOIST, OUTERMOST
 * rispetto alla conversione autoring→viewer (`world = Rx(questo angolo) ∘
 * authoringToViewerQuat() ∘ local`, non prima della conversione come le
 * altre correzioni — vedi `transforms/connectorPlacement.ts`). Frame mondo,
 * non di autoring: separato da `AUTHORING_TO_VIEWER_ROTATION_DEG` apposta.*/
export const JOIST_WORLD_X_FLIP_DEG = 180;

/**
 * Composition order of the GLB BODY quaternion with respect to the holes frame
 * holes (FIX A of the diagnosis):
 * - holes / normal / box fallback → conversion ONLY (`holesQuat`), zero euler;
 * - GLB body → euler applied IN frame authoring, then conversion in
 * viewer: `worldQ = conversion ∘ euler` (hierarchy: lock group with
 * quaternion = conversion, "mesh" child with quaternion = euler).
 * This respects the `Model3DPreview.calculateWorldQuaternion` template
 * ("base/conversion before, euler after"): in the template baseQ≈identity with the mock,
 * here the "base" is the Ry(∓90°) conversion.
 *
 * TODO(R7, to be calibrated in AC-1): if the GLB appears badly rotated, try
 * the reverse order `euler ∘ conversion`. The constant is documentary — the
 * composition happens in the `model/connectors.ts` hierarchy.*/
export const COMPOSE_ORDER = "euler-after-conversion" as const;

/**
 * Sign of the vertical offset of the LOCK-HEADER center (formula G5/C1).
 * `-1` → the center header is UNDER the top plane (lock-joist fit);
 * `+1` → above. EXPLICIT DECISION documented in `transforms/connectorFlush.ts`.
 *
 * TODO(R2, to be calibrated in AC-1): if the lock-header does not fit with
 * lock-joist in visual test, change sign here.*/
export const HEADER_FLUSH_SIGN = -1 as const;

/**
 * Distance lock ↔ top of beam (SPEC G5: "FUTURE parameter"; v1 = default,
 * not necessarily the same for header and joist). In v1 = 0: the flush follows
 * the G5 (C1) formula, not this offset.
 * TODO(future): when the parameter becomes available in the form, replace
 * defaults with form values (separated header/joist offsets).*/
export const OFFSET_FROM_TOP = { header: 0, joist: 0 } as const;

// ─── FUTURE geometric parameters (removed by fix P5; no longer dead constants) ──
//1) Offset from top per-role (`OFFSET_FROM_TOP_HEADER_MM` / `OFFSET_FROM_TOP_JOIST_MM`):
//    in v1, the only consumer is `OFFSET_FROM_TOP` (hook useLockScene → resolveScene.config);
//per-role values ​​will come from the form in the future (SPEC G5).
//2) G3 joint gap (0.5 mm): the two halves (joist/header) are modeled as
//coincident along X after the Rz(180°) branch and the G5/C1 flush; the real gap
//does not enter into the v1 calculation. Apply here (single source) when posting is needed.

/**
 * Default fastener length when fastener item is not available
 * (PLAN §2.6, SPEC §4.4). If the form provides the fastenerItemId with data
 * real, this constant is NOT used.*/
export const FASTENER_LENGTH_DEFAULT_MM = 80;

/** Alias ​​for the T6 request (more explicit name of the fastener default).*/
export const DEFAULT_FASTENER_LENGTH_MM = FASTENER_LENGTH_DEFAULT_MM;

/** Fixed real length of the beam in the viewer until it arrives from the form.*/
export const DEFAULT_BEAM_LENGTH_MM = 700;

/**
 * Vista esplosa (corretta 30-09-2026): le distanze non sono piu' fisse ma
 * derivano dagli ingombri reali (lunghezza vite, spessore assieme) — vedi
 * `model/explode.ts` per ordine, versi e formule. Qui resta solo lo spazio
 * minimo tra elementi consecutivi a `explodeFactor = 1`.*/
export const EXPLODE_DEFAULTS = {
  gapMm: 30,
  /** Linee tratteggiate guida (foro → vite esplosa), stesso stile di drei `<Line dashed>`.*/
  guideDash: { size: 4, gap: 3 },
} as const;

/**
 * Parametric fastener proportions when GLB is not available (28-09-2026,
 * richiesta utente: "migliora il modello, non perfetto, solo migliorato" —
 * niente asset GLB reali da caricare, solo geometria procedurale). Testa
 * esagonale (`headSegments: 6`, invece del cilindro liscio precedente) e
 * gambo "ad anelli" che approssima la filettatura (vedi
 * `components/fasteners/threadedShankGeometry.ts` per il perche' non e' un
 * elica vera): `threadPitchFactor`/`threadDepthFactor` sono relativi al
 * DIAMETRO reale della vite (non un mm fisso), cosi' la filettatura scala
 * in proporzione per viti di dimensioni diverse.*/
export const FASTENER_FALLBACK_DEFAULTS = {
  headDiameterFactor: 1.9,
  headHeightMm: 2,
  headSegments: 6,
  shankSegments: 10,
  /** Passo del filetto = diametro * questo fattore.*/
  threadPitchFactor: 0.55,
  /** Profondita' del filetto = raggio * questo fattore (0..1).*/
  threadDepthFactor: 0.3,
  /** Lunghezza della punta conica = diametro * questo fattore.*/
  tipLengthFactor: 1.5,
} as const;

/** Parameters of the procedural wood pattern of the beams.*/
export const WOOD_TEXTURE_DEFAULTS = {
  canvasSize: 512,
  lamellaCount: 6,
  grainStepPx: 11,
  grainLineWidth: 1.5,
  lamellaLineWidth: 2.5,
  endRingStartPx: 12,
  endRingStepPx: 18,
  endRingMaxPx: 600,
  endRingCenterOffsetPx: 120,
  endRingCenterVerticalOffsetPx: 20,
  repeatLengthMm: 250,
  repeatSectionMm: 100,
} as const;

/**
 * Default grid size (SPEC AC-1: grid on the X-Z plane a
 * y=0). `size` = side, `divisions` = number of subdivisions per side.
 *
 * Allargata 24-09-2026 (richiesta utente: "si perde la griglia base"):
 * 1000mm era piu' piccola dell'ingombro tipico di travi (fino a 700mm) +
 * quote all'estremita' lontana, quindi la griglia appariva come una toppa
 * scollegata dal modello invece che un riferimento a terra esteso. 3000mm
 * mantiene gli stessi 100mm/cella di prima (30 divisioni) su un'area 3×.*/
export const GRID_DEFAULTS = { size: 3000, divisions: 30 } as const;

/** Visual helper quotas, active only with `showDebug`.*/
export const CONSTRAINT_DEBUG_DEFAULTS = {
  connectorPlane: { width: 400, height: 300, opacity: 0.12 },
  connectorNormal: { length: 80, headLength: 12, headWidth: 6 },
  fastenerPointRadius: 4,
  alphaAxis: { length: 200, headLength: 14, headWidth: 7 },
  /**
   * Lunghezza (mm) dei tripodi RGB (`THREE.AxesHelper`, X=rosso Y=verde
   * Z=blu) disegnati su ciascun sistema di riferimento — piano globale,
   * connettori joist/header, travi joist/header (25-09-2026, richiesta
   * utente: verificare visivamente le trasformazioni durante l'indagine
   * incastro). `frame` per i frame "piccoli" (connettori), `plane` per il
   * piano globale (piu' grande, e' la radice di tutto).*/
  frameAxes: { frame: 40, plane: 100 },
} as const;

/**
 * URL del font delle etichette 3D, preso dal design system (che non esporta i
 * font e li nomina con un hash, es. `MuseoSans_700-SYNTY6G3.otf`): il glob
 * trova il file qualunque sia l'hash della versione installata e Vite lo
 * emette come asset, in dev e in build. `MuseoSans_700-*` non combacia con
 * `MuseoSans_700Italic-*`. Il fallback e' solo per un pacchetto senza il
 * font: l'etichetta non si vedrebbe, ma la scena (e il PDF) si genera.*/
const DESIGN_SYSTEM_LABEL_FONT = Object.values(
  import.meta.glob<string>("/node_modules/@rothoblaas/design-system/dist/fonts/MuseoSans_700-*.otf", {
    query: "?url",
    import: "default",
    eager: true,
  }),
)[0];

/**
 * Quote/dimensioni tecniche (3d/model/quotes.ts, 3d/components/quotes/).
 * `offsetMm`/`arcRadiusMm` sono i default di derivazione (sovrascrivibili per
 * singola quota); `minAngleDeg` e' la soglia sotto cui una quota angolare
 * viene saltata (stesso gate di `alphaDeg !== 0` in ConstraintDebug).*/
export const QUOTE_DEFAULTS = {
  offsetMm: 40,
  extensionOvershootMm: 8,
  /** Distanza (mm) fra linea di quota e bordo dell'etichetta, verso l'esterno.*/
  labelGapMm: 6,
  arrowLengthMm: 14,
  arrowWidthMm: 6,
  /** Segmenti radiali del cono di `Arrowhead.tsx` (qualita' visiva, non geometria di dominio).*/
  arrowConeSegments: 12,
  arcRadiusMm: 120,
  arcSegments: 32,
  minAngleDeg: 0.05,
  /**
   * Etichette: misure in MILLIMETRI mondo, non pixel schermo (30-09-2026).
   * Da quando l'etichetta e' testo WebGL (`QuoteLabel.tsx`) vive nello spazio
   * 3D come il resto della quota — deve quindi scalare con la scena, ed e' il
   * solo modo perche' rispetti l'occlusione e finisca nella cattura PDF.
   * `labelChipPaddingRatio` e' frazione di `labelSizeMm`; `labelChipDepthMm`
   * arretra il piano di sfondo dietro al testo (evita lo z-fighting).*/
  labelSizeMm: 18,
  labelChipPaddingRatio: 0.28,
  labelChipDepthMm: 0.5,
  /** Font del testo WebGL: lo stesso .otf del design system (troika legge OTF).*/
  labelFontUrl: DESIGN_SYSTEM_LABEL_FONT ?? "/fonts/MuseoSans_700.otf",
} as const;

/**
 * Frecce dei carichi applicati (3d/model/forces.ts, 3d/components/forces/),
 * 28-09-2026, richiesta utente. `shaftLengthMm` e' FISSA (non proporzionale
 * al valore in kN): i 4 carichi hanno ordini di grandezza molto diversi, una
 * lunghezza proporzionale renderebbe frecce enormi o invisibili a seconda del
 * caso — il valore numerico sta nell'etichetta, non nella lunghezza (stessa
 * scelta delle quote, che non scalano con la dimensione misurata).
 * `headLengthMm`/`headWidthMm` piu' grandi delle quote (`QUOTE_DEFAULTS`):
 * le forze sono un overlay opzionale evidente, non una misura tecnica fine.*/
export const FORCE_ARROW_DEFAULTS = {
  shaftLengthMm: 180,
  headLengthMm: 24,
  headWidthMm: 12,
  /** Etichetta in mm mondo (vedi `QUOTE_DEFAULTS.labelSizeMm`), un filo piu' grande.*/
  labelSizeMm: 22,
} as const;
